import { useEffect, useRef, useState } from "react";

// Iter-80 — Animated counter for calculator result figures.
//
// Smoothly tweens a dollar (or other numeric) value from the previously
// rendered figure to the new one when the user changes a slider/input.
// Pure CSS+rAF (no animation library). Respects `prefers-reduced-motion`
// — when set, the value snaps instantly to its final state.
//
// Why this exists: calculator result tiles benefit visually from a brief
// count-up — it signals "this number was just recomputed from your
// input" and is a known fintech conversion lift (Bankrate / Stripe
// calculators all use the same pattern). The animation is brief (~600ms)
// so it never blocks interaction.
//
// Props:
//   value         — target numeric value (required).
//   format        — function (num → string) for rendering. Defaults to
//                   USD with no decimals. Pass a custom one for %/decimals.
//   duration      — total tween duration in ms (default 600).
//   className     — passthrough.
//   testid        — passthrough.
//   prefix/suffix — optional fixed wrappers (e.g., "$", "/yr").
//
// Tabular-nums ensures all digit columns are equal-width so the number
// doesn't jitter horizontally as digits change during the tween.

const usdFmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

// Linear's recommended easing for value tweens — smooth start, smooth
// finish, no overshoot.
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export default function CountUp({
  value,
  format = usdFmt,
  duration = 600,
  className = "",
  testid,
  prefix = "",
  suffix = "",
  // Iter-412 — Opt-in scroll-triggered reveal. When true the counter
  // renders 0 and tweens 0 → value the first time it scrolls into
  // view (IntersectionObserver, fires once). Default false keeps the
  // original calculator behavior (snap on mount, tween on change).
  animateOnView = false,
  ...rest
}) {
  // Iter-423 — headless/crawler renders get the real value, not the 0 frame.
  const automated = typeof navigator !== "undefined" && Boolean(navigator.webdriver);
  const [display, setDisplay] = useState(animateOnView && !automated ? 0 : value);
  const fromRef = useRef(animateOnView ? 0 : value);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const spanRef = useRef(null);
  const [inView, setInView] = useState(() => {
    if (!animateOnView) return true;
    // Reduced-motion users get the final value immediately — no 0-flash.
    return (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );
  });

  useEffect(() => {
    if (!animateOnView || inView) return undefined;
    const el = spanRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [animateOnView, inView]);

  useEffect(() => {
    // Not yet revealed — hold at the baseline until the observer fires.
    if (!inView) return undefined;

    // First render — no animation, no flash. Persist the new target as
    // the "from" baseline for any subsequent update.
    if (fromRef.current === value) {
      setDisplay(value);
      return undefined;
    }

    // Respect the user's accessibility preference. Snap, don't tween.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      fromRef.current = value;
      setDisplay(value);
      return undefined;
    }

    const from = fromRef.current;
    const to = value;
    startRef.current = null;

    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, inView]);

  const rendered =
    typeof display === "number" && Number.isFinite(display)
      ? format(display)
      : format(value);

  return (
    <span
      ref={spanRef}
      data-testid={testid}
      className={`tabular-nums ${className}`.trim()}
      {...rest}
    >
      {prefix}
      {rendered}
      {suffix}
    </span>
  );
}
